import type { ProjectDocument } from '@timelog/pm-shared';
import { useSyncExternalStore } from 'react';

import { createSettingsSample } from '../sampleData';
import type { SettingsSample } from '../sampleData';

import { parseChangeLog } from './changeLog';
import type { ChangeEntry } from './changeLog';
import { defaultDetailsValues, parseDetailsPatch } from './detailsValues';
import type { DetailsPatch, DetailsValues } from './detailsValues';

/**
 * Project details' values, per browser and per project.
 *
 * A module-level store, as `lib/sampleProject.ts` is, because components in
 * different subtrees must agree at once: the header on every tab names the
 * project manager, and the Project details page changes them. Per-component
 * `useState` would leave the header stale until a reload.
 */
export const detailsStorageKey = (projectId: string): string => `pm.details.${projectId}`;

let sample: SettingsSample | null = null;

/** v1's sample (about 200 people), built once — every project shares it. */
export const detailsSample = (): SettingsSample => {
  sample ??= createSettingsSample();
  return sample;
};

const listeners = new Set<() => void>();

/*
  getSnapshot runs on every render, so each project's patch is parsed once and
  cached; the cache is what makes the snapshot stable between renders.
*/
const cache = new Map<string, DetailsPatch>();

/* Guarded: localStorage throws in private mode and on a full quota. */
const readPatch = (projectId: string): DetailsPatch => {
  try {
    const stored = window.localStorage.getItem(detailsStorageKey(projectId));
    return stored === null ? {} : parseDetailsPatch(JSON.parse(stored), detailsSample());
  } catch {
    return {};
  }
};

const getPatch = (projectId: string): DetailsPatch => {
  let patch = cache.get(projectId);
  if (patch === undefined) {
    patch = readPatch(projectId);
    cache.set(projectId, patch);
  }
  return patch;
};

const notify = () => {
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Merges `next` into the project's stored patch, and tells every reader. */
export const writeDetailsPatch = (projectId: string, next: DetailsPatch): void => {
  const merged = { ...getPatch(projectId), ...next };
  cache.set(projectId, merged);
  try {
    window.localStorage.setItem(detailsStorageKey(projectId), JSON.stringify(merged));
  } catch {
    // Private mode or a full quota: the change holds for this visit only.
  }
  notify();
};

/** Forgets every change for the project, and its history, back to the sample defaults. */
export const resetDetails = (projectId: string): void => {
  cache.set(projectId, {});
  changeCache.set(projectId, []);
  try {
    window.localStorage.removeItem(detailsStorageKey(projectId));
    window.localStorage.removeItem(changesStorageKey(projectId));
  } catch {
    // As above: the reset holds for this visit only.
  }
  notify();
};

/**
 * The project's values: its stored patch over the defaults. Pass the project
 * document where the plan's dates matter (the When card); the header, which
 * only names the project manager, needs none.
 */
export const useDetailsValues = (
  projectId: string,
  document?: Pick<ProjectDocument, 'plan'>,
): DetailsValues => {
  const patch = useSyncExternalStore(subscribe, () => getPatch(projectId));
  return { ...defaultDetailsValues(detailsSample(), document), ...patch };
};

/** The project manager's name, for the header's second line. */
export const useProjectManagerName = (projectId: string): string => {
  const { projectManagerId } = useDetailsValues(projectId);
  return detailsSample().employees.find((employee) => employee.id === projectManagerId)?.name ?? '';
};

/*
  What's changed (brief §3): the saved changes, newest first, per project —
  kept beside the values, and read the same way.
*/
export const changesStorageKey = (projectId: string): string => `pm.details.${projectId}.changes`;

const changeCache = new Map<string, ChangeEntry[]>();

const readChanges = (projectId: string): ChangeEntry[] => {
  try {
    const stored = window.localStorage.getItem(changesStorageKey(projectId));
    return stored === null ? [] : parseChangeLog(JSON.parse(stored));
  } catch {
    return [];
  }
};

const getChanges = (projectId: string): ChangeEntry[] => {
  let changes = changeCache.get(projectId);
  if (changes === undefined) {
    changes = readChanges(projectId);
    changeCache.set(projectId, changes);
  }
  return changes;
};

/** Puts a saved change at the top of the project's history. */
export const appendChange = (projectId: string, entry: ChangeEntry): void => {
  const next = [entry, ...getChanges(projectId)];
  changeCache.set(projectId, next);
  try {
    window.localStorage.setItem(changesStorageKey(projectId), JSON.stringify(next));
  } catch {
    // As with the values: the history holds for this visit only.
  }
  notify();
};

export const useChangeLog = (projectId: string): ChangeEntry[] =>
  useSyncExternalStore(subscribe, () => getChanges(projectId));
